import { useState } from 'react';
import type { SortingState, VisibilityState } from '@tanstack/react-table';

import {
  MerchantOrderStatusEnum,
  type OpsOrderFilter,
} from '@/graphql/generatedTypes';
import { readStorage, usePersistedState } from '@/hooks/usePersistedState';
import { useOpsOrdersSearchQuery } from '@/graphql/queries/opsOrdersSearch.generated';
import { useCancelOpsOrderMutation } from '@/graphql/mutations/cancelOpsOrder.generated';
import { useExportOpsOrderMutation } from '@/graphql/mutations/exportOpsOrder.generated';
import { useUpdateOpsOrderStatusMutation } from '@/graphql/mutations/updateOpsOrderStatus.generated';
import type {
  FilterDefinition,
  ActiveFilter,
} from '../components/table/OrderToolbar';
import type { ContainerTypeComboboxProps } from '../components/table/ContainerTypeDropdown';
import {
  buildOrderListFilter,
  buildOrderListSort,
  getDefaultOrderListActiveFilters,
  getOrderListFilterDefinitions,
  mergeOrderFilters,
  restoreActiveFilters,
  type OrderSystemType,
} from '../utils/orderListFilters';
import { toast } from 'sonner';
import i18n from '@/utils/i18n';

type ContainerTypes = ContainerTypeComboboxProps['containerTypes'];

interface UseOrderListStateOptions {
  /** Base filter scoping which orders to show (e.g. `{ system: 'OPEN_SYSTEM' }`) */
  baseFilter: OpsOrderFilter;
  /** Determines default filter definitions and active filters */
  systemType: OrderSystemType;
  /** Override available filter definitions (useful when populating options dynamically) */
  availableFilters?: FilterDefinition[];
  /** Override default active filters */
  defaultActiveFilters?: ActiveFilter[];
  /** Default column visibility when nothing is persisted */
  defaultColumnVisibility?: VisibilityState;
  /** Page size. Default 20. */
  pageSize?: number;
}

export function useOrderListState({
  baseFilter,
  systemType,
  availableFilters: availableFiltersProp,
  defaultActiveFilters,
  defaultColumnVisibility,
  pageSize = 20,
}: UseOrderListStateOptions) {
  const resolvedAvailableFilters =
    availableFiltersProp ?? getOrderListFilterDefinitions(systemType);
  const resolvedDefaultActive =
    defaultActiveFilters ?? getDefaultOrderListActiveFilters(systemType);

  // -- Storage keys --
  const colVisKey = `order-list-${systemType.toLowerCase()}-col-vis`;
  const filtersKey = `order-list-${systemType.toLowerCase()}-filters`;

  // -- Pagination --
  const [currentPage, setCurrentPage] = useState(1);

  // -- Sorting --
  const [_sorting, setSorting] = useState<SortingState>([]);
  const sorting: SortingState =
    _sorting.length === 0 ? [{ id: 'last_updated', desc: true }] : _sorting;

  // -- Column visibility (persisted) --
  const [columnVisibility, setColumnVisibility] =
    usePersistedState<VisibilityState>(
      colVisKey,
      (stored) =>
        (stored as VisibilityState | null) ?? defaultColumnVisibility ?? {},
    );

  // -- Filters (persisted) --
  const [activeFilters, setActiveFilters] = usePersistedState<ActiveFilter[]>(
    filtersKey,
    (stored) => {
      const restored = restoreActiveFilters(stored, resolvedAvailableFilters);
      if (restored.length > 0) return restored;

      // Legacy migration: read old key format
      const legacyKey = `order-list-${systemType.toLowerCase()}-filter-ids`;
      const legacyIds = readStorage(legacyKey);
      if (Array.isArray(legacyIds)) {
        localStorage.removeItem(legacyKey);
        const migrated = legacyIds
          .filter((id) => resolvedAvailableFilters.some((f) => f.id === id))
          .map((id) => ({ id, value: '__all__' }));
        if (migrated.length > 0) return migrated;
      }

      return resolvedDefaultActive;
    },
  );

  // -- Container types --
  const [selectedContainerTypes, setSelectedContainerTypes] =
    useState<ContainerTypes>([]);

  const handleContainerTypeChange = (cts: ContainerTypes) => {
    setSelectedContainerTypes(cts);
    setCurrentPage(1);
  };

  // -- Build query params --
  const activeFilter = buildOrderListFilter(
    activeFilters,
    selectedContainerTypes.map((ct) => ct.id),
  );
  const filter = mergeOrderFilters(baseFilter, activeFilter);
  const sort = buildOrderListSort(sorting);

  // -- Query --
  const { data, loading, error, refetch } = useOpsOrdersSearchQuery({
    variables: {
      filter,
      sort,
      take: pageSize,
      skip: (currentPage - 1) * pageSize,
    },
    fetchPolicy: 'cache-and-network',
  });

  const orders = data?.opsOrdersSearch.items ?? [];
  const totalCount = data?.opsOrdersSearch.total ?? 0;

  // -- Mutations --
  const [cancelOpsOrder] = useCancelOpsOrderMutation();
  const [exportOrder] = useExportOpsOrderMutation();
  const [updateStatus] = useUpdateOpsOrderStatusMutation();

  const handleCancel = async (ids: string[]) => {
    try {
      await cancelOpsOrder({ variables: { filter, ids } });
      toast.success(i18n.t('Order:toastCancelSuccess'));
      refetch();
    } catch {
      toast.error(i18n.t('Order:toastCancelError'));
    }
  };

  const handleUpdateStatus = async (
    ids: string[],
    status: MerchantOrderStatusEnum,
  ) => {
    try {
      await updateStatus({ variables: { filter, ids, status } });
      toast.success(i18n.t('Order:toastStatusUpdateSuccess'));
      refetch();
    } catch {
      toast.error(i18n.t('Order:toastStatusUpdateError'));
    }
  };

  const handleExport = async (ids: string[]) => {
    let result: Awaited<ReturnType<typeof exportOrder>>;

    try {
      result = await exportOrder({ variables: { filter, ids } });
    } catch {
      toast.error(i18n.t('Order:toastExportError'));
      return;
    }

    const url = result.data?.exportOpsOrder;
    if (url) {
      window.open(url, '_blank');
    }
  };

  // -- Filter handlers --
  const handleFilterChange = (filterId: string, value: string) => {
    setActiveFilters((prev) =>
      prev.map((f) => (f.id === filterId ? { ...f, value } : f)),
    );
    setCurrentPage(1);
  };

  const handleFilterAdd = (filterId: string) => {
    setActiveFilters((prev) => {
      if (prev.some((f) => f.id === filterId)) return prev;
      return [...prev, { id: filterId, value: '__all__' }];
    });
    setCurrentPage(1);
  };

  const handleFilterRemove = (filterId: string) => {
    const def = resolvedAvailableFilters.find((f) => f.id === filterId);
    if (def?.isRemovable === false) return;
    setActiveFilters((prev) => prev.filter((f) => f.id !== filterId));
    setCurrentPage(1);
  };

  return {
    // Data
    orders,
    totalCount,
    loading,
    error,
    refetch,

    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,

    // Sorting
    sorting,
    setSorting,

    // Column visibility
    columnVisibility,
    setColumnVisibility,

    // Filters
    availableFilters: resolvedAvailableFilters,
    activeFilters,
    setActiveFilters,
    handleFilterChange,
    handleFilterAdd,
    handleFilterRemove,

    // Container types
    selectedContainerTypes,
    handleContainerTypeChange,

    // Mutations
    handleCancel,
    handleUpdateStatus,
    handleExport,

    // Computed filter (needed by consumers for their own mutations)
    filter,
  };
}
